import type {
  ReploSimpleProject,
  ReploSimpleProjectWithStats,
} from "schemas/generated/project";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import EditableProjectName from "@v1/components/dashboard/projects/EditableProjectName";
import ShopifyButton from "@v1/components/dashboard/projects/GalleryProjectItem/ShopifyButton";
import ProjectMenu from "@v1/components/dashboard/projects/ProjectMenu";
import { useEditedTimeString } from "@v1/hooks/useEditedTimeString";
import { getProjectName } from "@v2/utils/project-utils";
import { isShopifyIntegrationEnabled } from "schemas/utils";

type ProjectItemProps = {
  project: ReploSimpleProjectWithStats | ReploSimpleProject;
  onSelectProject: (e: React.MouseEvent) => void;
  className?: string;
  shouldShowCreatedAt?: boolean;
  showWorkspaceColumn?: boolean;
  hideEllipsisButton?: boolean;
  allowsRenaming: boolean;
};

const ListProjectItem: React.FC<ProjectItemProps> = ({
  project,
  onSelectProject,
  className,
  shouldShowCreatedAt = true,
  showWorkspaceColumn = false,
  hideEllipsisButton = false,
  allowsRenaming,
}) => {
  const editedTimeString = useEditedTimeString(
    project.lastEditedAt,
    project.createdAt,
  );
  const [isRenaming, setIsRenaming] = React.useState(false);
  const uniqueId = `project-${project.id}-${React.useId()}`;

  const handleStartRenaming = () => {
    setIsRenaming(true);
  };

  const handleFinishRenaming = () => {
    setIsRenaming(false);
  };

  return (
    <div
      role="button"
      className={twMerge(
        "text-default grid w-full grid-cols-12 items-center justify-center gap-2 py-4 pl-4 pr-4 text-sm cursor-pointer hover:bg-selectable-hover",
        className,
      )}
      onClick={onSelectProject}
      id={uniqueId}
    >
      <div className="col-span-3 flex flex-row items-center gap-x-4 text-left">
        <EditableProjectName
          projectId={project.id}
          initialProjectName={getProjectName(project)}
          isRenaming={allowsRenaming && isRenaming}
          onFinishEditing={handleFinishRenaming}
        />
      </div>
      {showWorkspaceColumn ? (
        <div
          className="col-span-3 flex flex-row items-center gap-x-4 text-left"
          aria-describedby={uniqueId}
        >
          <span aria-hidden>{project.ownerWorkspace?.name ?? "-"}</span>
          <span className="sr-only">
            {project.ownerWorkspace?.name != null
              ? `Workspace ${project.ownerWorkspace?.name}`
              : "No workspace"}
          </span>
        </div>
      ) : null}
      {shouldShowCreatedAt && (
        <div className="text-muted col-span-3 flex">
          <span className="sr-only">Edited</span>
          {editedTimeString.charAt(0).toUpperCase() + editedTimeString.slice(1)}
        </div>
      )}
      <div
        className={twMerge(
          "flex flex-row items-center",
          showWorkspaceColumn ? "col-span-2" : "col-span-3",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <ShopifyButton
          isConnected={isShopifyIntegrationEnabled(project)}
          shopifyUrl={project.integrations?.shopify?.store.shopifyUrl}
          project={project}
        />
      </div>
      {!hideEllipsisButton && (
        <div
          className={twMerge(
            "flex",
            showWorkspaceColumn ? "col-span-1" : "col-span-3",
          )}
        >
          <ProjectMenu
            project={project}
            allowsRenaming={allowsRenaming}
            onRenameOpen={handleStartRenaming}
          />
        </div>
      )}
    </div>
  );
};

export default ListProjectItem;
